import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Building2,
  CheckCircle,
  Code2,
  Cog,
  Headphones,
  LifeBuoy,
  Mail,
  MapPin,
  Menu,
  MessageSquareText,
  Phone,
  PhoneCall,
  Rocket,
  ShieldCheck,
  Sparkles,
  Workflow,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import services from "./content/services";
import locations from "./content/locations";
import { agentsPillar, agentIndustries } from "./content/agents";
import { customSolutions, customSolutionsPillar } from "./content/customSolutions";
import ContactForm from "./components/ContactForm";
import { TAGLINES, getActiveVariant } from "./lib/variant";
import { setVariantProperty, trackEvent } from "./lib/analytics";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildLocalBusinessSchema,
  buildLocationSchema,
  buildOrganizationSchema,
  buildServiceSchema,
  nationalAreaServed,
} from "./seo/schema";

const siteUrl = "https://www.tier-1.io";
const contactEmail = "contact@tier-1.io";
const displayPhone = "+1 (909) 720-8731";
const contactPhone = `+1${displayPhone.replace(/\D/g, "").slice(1)}`;
const contactHref = "/contact/";

const primaryOfferSlugs = [
  "ai-discovery-call",
  "done-for-you-ai-implementation",
  "custom-ai-engagements",
  "ai-ops-support-retainer",
];

const offerIcons = {
  "ai-discovery-call": MessageSquareText,
  "done-for-you-ai-implementation": Rocket,
  "custom-ai-engagements": BrainCircuit,
  "ai-ops-support-retainer": LifeBuoy,
  "small-business-ai-automation": Workflow,
  "chatgpt-consultant-small-business": Bot,
  "ai-workflow-automation": Zap,
  "email-hosting": Mail,
  "web-development": Code2,
  "it-support": Wrench,
};

const offerKickers = {
  "ai-discovery-call": "Consultation",
  "done-for-you-ai-implementation": "Build",
  "custom-ai-engagements": "Bespoke",
  "ai-ops-support-retainer": "Ongoing",
};

const homepageOffers = services
  .filter((service) => primaryOfferSlugs.includes(service.slug))
  .sort((a, b) => primaryOfferSlugs.indexOf(a.slug) - primaryOfferSlugs.indexOf(b.slug));

const useCases = [
  "Turn sales calls into follow-up emails, tasks, and CRM updates",
  "Qualify website leads and draft the next best response",
  "Convert voice notes into structured reminders and project tasks",
  "Summarize inboxes, tickets, and documents into action queues",
  "Generate proposals, scopes of work, and status updates from notes",
  "Build internal chat assistants over your processes and knowledge base",
];

const processSteps = [
  {
    title: "Find the workflow",
    description:
      "We look for repeatable, annoying, high-leverage work instead of chasing AI hype.",
  },
  {
    title: "Design the simplest system",
    description:
      "We choose boring, maintainable tools first and go custom only when the business case is clear.",
  },
  {
    title: "Build and integrate",
    description:
      "We connect the apps, prompts, APIs, automations, and guardrails that make the workflow usable.",
  },
  {
    title: "Document and support",
    description:
      "You get a working system, plain-English docs, and optional support so it keeps creating value.",
  },
];

const stats = [
  { value: "20+", label: "Years building production systems" },
  { value: "4", label: "Clear ways to engage" },
  { value: "1", label: "Messy workflow needed to start" },
  { value: "0", label: "AI theater required" },
];

const normalizePath = () => window.location.pathname.replace(/^\/+|\/+$/g, "");
const pageUrl = (path = "/") => `${siteUrl}${path.startsWith("/") ? path : `/${path}`}`;

const setMetaTag = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    if (selector.includes("property=")) {
      element.setAttribute("property", selector.match(/property="([^"]+)"/)?.[1] || "");
    } else {
      element.setAttribute("name", selector.match(/name="([^"]+)"/)?.[1] || "");
    }
    document.head.appendChild(element);
  }

  element.setAttribute(attribute, value);
};

const usePageMeta = ({ title, description, path = "/", schema = [] }) => {
  useEffect(() => {
    document.title = title;
    setMetaTag('meta[name="description"]', "content", description);
    setMetaTag('meta[property="og:title"]', "content", title);
    setMetaTag('meta[property="og:description"]', "content", description);
    setMetaTag('meta[property="og:url"]', "content", pageUrl(path));

    document.querySelectorAll("script[data-tier-schema]").forEach((script) => script.remove());
    schema.filter(Boolean).forEach((schemaItem) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.tierSchema = "true";
      script.text = JSON.stringify(schemaItem);
      document.head.appendChild(script);
    });
  }, [title, description, path, schema]);
};

const useVariant = () => {
  const [variant, setVariant] = useState("control");

  useEffect(() => {
    const active = getActiveVariant();
    setVariant(active);
    setVariantProperty(active);
    trackEvent("tagline_variant", { variant: active });
  }, []);

  return variant;
};

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="max-w-3xl">
    <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">{eyebrow}</p>
    <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-5xl">{title}</h2>
    {description && <p className="mt-5 text-lg leading-8 text-slate-300">{description}</p>}
  </div>
);

const CtaButton = ({ children = "Book an AI Discovery Call", className = "" }) => (
  <a
    href={contactHref}
    className={`inline-flex items-center justify-center rounded-full bg-cyan-400 px-7 py-4 font-semibold text-slate-950 transition hover:bg-cyan-300 ${className}`}
  >
    {children}
    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
  </a>
);

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navItems = [
    { label: "Services", href: "/services/" },
    { label: "Voice Agents", href: "/conversational-ai-agents/" },
    { label: "Use Cases", href: "/#use-cases" },
    { label: "Locations", href: "/locations/" },
    { label: "Process", href: "/#process" },
    { label: "Contact", href: "/contact/" },
  ];

  const handleCloseMobileMenu = () => setMobileMenuOpen(false);
  const handleToggleMobileMenu = () => setMobileMenuOpen((isOpen) => !isOpen);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center gap-3" aria-label="Tier-1.io home">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400 text-slate-950">
              <Bot className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight">Tier-1.io</div>
              <div className="text-xs uppercase tracking-[0.25em] text-cyan-300">AI Consultancy</div>
            </div>
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} className="text-sm font-medium text-slate-300 transition hover:text-white">
                {item.label}
              </a>
            ))}
            <a href={contactHref} className="rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300">
              Book Discovery
            </a>
          </div>

          <button
            type="button"
            onClick={handleToggleMobileMenu}
            className="rounded-lg p-2 text-slate-200 transition hover:bg-white/10 md:hidden"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-white/10 bg-slate-950 px-4 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={handleCloseMobileMenu}
                  className="rounded-lg px-3 py-2 text-base font-medium text-slate-200 hover:bg-white/10"
                >
                  {item.label}
                </a>
              ))}
              <a href={contactHref} onClick={handleCloseMobileMenu} className="rounded-lg bg-cyan-400 px-3 py-3 text-center font-semibold text-slate-950">
                Book Discovery
              </a>
            </div>
          </div>
        )}
      </nav>

      {children}

      <footer className="border-t border-white/10 py-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 text-sm text-slate-400 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <div className="flex items-center gap-2">
            <Cog className="h-4 w-4 text-cyan-300" aria-hidden="true" />
            <span>Tier-1.io AI Consultancy</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="/services/" className="transition hover:text-white">Services</a>
            <a href="/custom-solutions/" className="transition hover:text-white">Custom Solutions</a>
            <a href="/contact/" className="transition hover:text-white">Contact</a>
          </div>
          <p>&copy; 2026 Tier-1.io. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const OfferCard = ({ offer, numbered = false }) => {
  const Icon = offerIcons[offer.slug] || Workflow;
  const category = offer.category || offerKickers[offer.slug] || offer.kicker;

  return (
    <article className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition hover:-translate-y-1 hover:border-cyan-300/40 hover:bg-white/[0.06]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-400 text-slate-950">
          <Icon className="h-7 w-7" aria-hidden="true" />
        </div>
        <span className="rounded-full border border-cyan-300/30 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-200">
          {numbered ? `No. ${String(primaryOfferSlugs.indexOf(offer.slug) + 1).padStart(2, "0")}` : category}
        </span>
      </div>
      <p className="mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">{category}</p>
      <h3 className="mt-2 text-2xl font-bold text-white">{offer.title}</h3>
      <p className="mt-3 leading-7 text-slate-300">{offer.heroDescription || offer.description}</p>
      <ul className="mt-6 space-y-3">
        {(offer.outcomes || offer.deliverables || []).slice(0, 4).map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-slate-300">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-cyan-300" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <a href={`/${offer.slug}/`} className="mt-8 inline-flex items-center font-semibold text-cyan-300 hover:text-cyan-200">
        {offer.cta || "Learn more"}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </a>
    </article>
  );
};

const HomePage = () => {
  const variant = useVariant();
  const schema = useMemo(() => [buildOrganizationSchema(), buildLocalBusinessSchema()], []);
  usePageMeta({
    title: "Tier-1.io AI Consultancy | Practical AI Automation for Small Business",
    description:
      "Tier-1.io helps small businesses and lean teams turn repeatable workflows into useful AI systems, automation, custom AI engagements, and ongoing support.",
    path: "/",
    schema,
  });

  return (
    <Layout>
      <main>
        <section id="home" className="relative overflow-hidden">
          <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:py-28">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-medium text-cyan-200">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Practical AI systems for lean teams and small businesses
              </div>
              <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
                {TAGLINES[variant]}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                Tier-1.io helps owners and operators find the boring, profitable places AI can save time, reduce manual work, and improve follow-through — then we build and support the systems.
              </p>
              <p className="mt-4 max-w-2xl text-sm font-medium uppercase tracking-[0.2em] text-cyan-200">
                Serving Cherry Valley, Beaumont, Calimesa, Redlands, and the Inland Empire.
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <CtaButton />
                <a href="/services/" className="inline-flex items-center justify-center rounded-full border border-white/20 px-7 py-4 font-semibold text-white transition hover:bg-white/10">
                  See What We Build
                </a>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-cyan-950/40 backdrop-blur">
              <div className="rounded-2xl bg-slate-900 p-6">
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <Workflow className="h-6 w-6 text-cyan-300" aria-hidden="true" />
                  <h2 className="text-xl font-semibold text-white">Bring us one messy workflow</h2>
                </div>
                <div className="mt-6 space-y-4">
                  {[
                    "The work your team repeats every week",
                    "The follow-up that falls through the cracks",
                    "The spreadsheet, inbox, or CRM process everyone hates",
                    "The customer questions you answer over and over",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle className="mt-1 h-5 w-5 flex-none text-cyan-300" aria-hidden="true" />
                      <p className="text-slate-300">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-2xl bg-cyan-400/10 p-4 text-sm leading-6 text-cyan-100">
                  We will tell you what AI can realistically do, what not to automate, and the fastest path to a useful result.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/[0.03] py-10">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 text-center sm:px-6 lg:grid-cols-4 lg:px-8">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-bold text-cyan-300 sm:text-4xl">{stat.value}</div>
                <div className="mt-2 text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section id="services" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Services"
            title="Four ways to make AI useful — from first call to ongoing support."
            description="Start with clarity, move into implementation, go custom when the business case is there, and keep critical workflows supported after launch."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {homepageOffers.map((offer) => <OfferCard key={offer.slug} offer={offer} numbered />)}
          </div>
        </section>

        <VoiceAgentsBanner />

        <section id="use-cases" className="bg-slate-900/70 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <SectionHeading
                eyebrow="What we build"
                title="AI automation with a business outcome attached."
                description="We focus on workflows where a better system creates time, consistency, better follow-up, or new sales capacity."
              />
              <div className="grid gap-4 sm:grid-cols-2">
                {useCases.map((useCase) => (
                  <div key={useCase} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                    <Zap className="mb-4 h-6 w-6 text-cyan-300" aria-hidden="true" />
                    <p className="font-medium leading-7 text-slate-200">{useCase}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Process"
            title="Useful before impressive."
            description="The goal is not to add AI everywhere. The goal is to ship reliable systems that remove friction and create measurable value."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((step, index) => (
              <div key={step.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 font-bold text-slate-950">{index + 1}</div>
                <h3 className="mt-6 text-xl font-bold text-white">{step.title}</h3>
                <p className="mt-3 leading-7 text-slate-300">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        <CtaSection />
        <ContactSection />
      </main>
    </Layout>
  );
};

const CtaSection = () => (
  <section className="bg-cyan-400 py-16 text-slate-950">
    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Have one workflow that should be easier?</h2>
        <p className="mt-3 max-w-2xl text-lg text-slate-800">
          Send us the messy version. We will help you figure out whether AI, automation, better process, or no change at all is the right answer.
        </p>
      </div>
      <a href={contactHref} className="inline-flex items-center rounded-full bg-slate-950 px-7 py-4 font-semibold text-white transition hover:bg-slate-800">
        Start the Conversation
        <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
      </a>
    </div>
  </section>
);

const ContactSection = () => (
  <section id="contact" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
    <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
      <div>
        <SectionHeading
          eyebrow="Contact"
          title="Let’s make AI useful for your business."
          description="Tell us what is manual, repetitive, or slipping through the cracks. We will respond with the best next step."
        />
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-900 p-6">
            <Mail className="h-7 w-7 text-cyan-300" aria-hidden="true" />
            <div className="mt-4 font-semibold text-white">Email</div>
            <div className="mt-1 text-slate-300">{contactEmail}</div>
          </div>
          <a href={`tel:${contactPhone}`} className="rounded-2xl bg-slate-900 p-6 transition hover:bg-slate-800">
            <Phone className="h-7 w-7 text-cyan-300" aria-hidden="true" />
            <div className="mt-4 font-semibold text-white">Phone</div>
            <div className="mt-1 text-slate-300">{displayPhone}</div>
          </a>
        </div>
        <div className="mt-4 rounded-2xl bg-cyan-400/10 p-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-cyan-300" aria-hidden="true" />
            <h3 className="font-semibold text-white">Built by operators, not tourists.</h3>
          </div>
          <p className="mt-3 leading-7 text-slate-300">
            Tier-1.io brings infrastructure, application, automation, and support experience to the AI tools your business actually needs to rely on.
          </p>
        </div>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8">
        <ContactForm />
      </div>
    </div>
  </section>
);

const ServicesIndexPage = () => {
  const schema = useMemo(
    () => [
      buildOrganizationSchema(),
      buildLocalBusinessSchema(),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/services/" },
      ]),
    ],
    []
  );
  usePageMeta({
    title: "AI Consulting Services | Tier-1.io",
    description: "Explore Tier-1.io AI discovery, implementation, custom AI engagement, support retainer, and local small-business AI automation services.",
    path: "/services/",
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow="Services" title="Practical AI services for small businesses and lean teams." description="Pick the entry point that matches where you are: clarity, implementation, custom build, ongoing support, or local SEO-focused workflow automation help." />
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {services.map((service) => <OfferCard key={service.slug} offer={service} />)}
          </div>
        </section>
        <CtaSection />
      </main>
    </Layout>
  );
};

const customSolutionOffers = [
  ...customSolutions,
  services.find((service) => service.slug === "custom-ai-engagements"),
].filter(Boolean);

const CustomSolutionsPage = () => {
  const schema = useMemo(
    () => [
      buildOrganizationSchema(),
      buildLocalBusinessSchema(),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Custom Solutions", url: "/custom-solutions/" },
      ]),
    ],
    []
  );
  usePageMeta({
    title: customSolutionsPillar.title,
    description: customSolutionsPillar.metaDescription,
    path: "/custom-solutions/",
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero
          eyebrow={customSolutionsPillar.heroKicker}
          title={customSolutionsPillar.heroTitle}
          description={customSolutionsPillar.heroDescription}
        />
        <section className="mx-auto max-w-4xl px-4 pt-16 sm:px-6 lg:px-8">
          <p className="text-lg leading-8 text-slate-300">{customSolutionsPillar.intro}</p>
        </section>
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {customSolutionOffers.map((offer) => <OfferCard key={offer.slug} offer={offer} />)}
          </div>
        </section>
        <CtaSection />
      </main>
    </Layout>
  );
};

const PageHero = ({ eyebrow, title, description, cta }) => (
  <section className="relative overflow-hidden border-b border-white/10">
    <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-cyan-400/20 blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">{eyebrow}</p>
      <h1 className="mt-4 max-w-4xl text-4xl font-bold tracking-tight text-white sm:text-6xl">{title}</h1>
      <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300 sm:text-xl">{description}</p>
      <div className="mt-10"><CtaButton>{cta || "Book an AI Discovery Call"}</CtaButton></div>
    </div>
  </section>
);

const ServicePage = ({ service }) => {
  const Icon = offerIcons[service.slug] || Workflow;
  const schema = useMemo(
    () => [
      buildServiceSchema({
        name: service.title,
        slug: service.slug,
        url: pageUrl(`/${service.slug}/`),
        description: service.metaDescription || service.heroDescription,
        serviceType: service.title,
        areaServed: service.national ? nationalAreaServed : undefined,
      }),
      buildFaqSchema(service.faqs || []),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/services/" },
        { name: service.title, url: `/${service.slug}/` },
      ]),
    ],
    [service]
  );
  usePageMeta({
    title: `${service.title} | Tier-1.io`,
    description: service.metaDescription || service.heroDescription,
    path: `/${service.slug}/`,
    schema,
  });

  return (
    <Layout>
      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_0.8fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">{service.category || service.kicker}</p>
              <h1 className="mt-4 text-4xl font-bold tracking-tight text-white sm:text-6xl">{service.heroTitle || service.title}</h1>
              <p className="mt-6 text-lg leading-8 text-slate-300 sm:text-xl">{service.heroDescription}</p>
              {service.format && <p className="mt-5 rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-4 leading-7 text-cyan-100"><strong>Format:</strong> {service.format}</p>}
              <div className="mt-8"><CtaButton>{service.cta || "Book an AI Discovery Call"}</CtaButton></div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8">
              <Icon className="h-10 w-10 text-cyan-300" aria-hidden="true" />
              <h2 className="mt-6 text-2xl font-bold text-white">Best for</h2>
              <ul className="mt-5 space-y-4">
                {(service.bestFor || []).map((item) => (
                  <li key={item} className="flex gap-3 text-slate-300">
                    <CheckCircle className="mt-1 h-5 w-5 flex-none text-cyan-300" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <DetailGrid title="Outcomes" items={service.outcomes || []} />
        <ProcessDetail steps={service.process || []} />
        <DetailGrid title="Deliverables" items={service.deliverables || []} />
        <ComparisonTable comparison={service.comparison} />
        <FaqSection faqs={service.faqs || []} />
        <CtaSection />
      </main>
    </Layout>
  );
};

const comparisonToneText = {
  good: "text-emerald-300",
  mid: "text-amber-300",
  bad: "text-rose-300",
  neutral: "text-slate-300",
};

const ComparisonTable = ({ comparison }) => {
  if (!comparison) return null;
  const { title, description, columns, rows, footnote } = comparison;
  return (
    <section className="bg-slate-900/70 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white">{title}</h2>
        {description && <p className="mt-4 max-w-3xl leading-8 text-slate-300">{description}</p>}
        <div className="mt-8 overflow-x-auto rounded-3xl border border-white/10">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead>
              <tr className="bg-white/[0.04]">
                <th scope="col" className="sticky left-0 z-10 bg-slate-900 p-4" aria-label="Comparison criteria" />
                {columns.map((column, index) => (
                  <th
                    key={column.name}
                    scope="col"
                    className={`p-4 align-bottom text-base font-bold ${index === 0 ? "text-cyan-300" : "text-white"}`}
                  >
                    {column.name}
                    {column.href && (
                      <a
                        href={column.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block text-xs font-medium text-cyan-300 hover:text-cyan-200"
                      >
                        View pricing →
                      </a>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-white/10">
                  <th scope="row" className="sticky left-0 z-10 bg-slate-900 p-4 align-top font-semibold text-slate-200">
                    {row.label}
                  </th>
                  {row.values.map((value, index) => {
                    const cell = typeof value === "string" ? { text: value } : value;
                    return (
                      <td
                        key={`${row.label}-${index}`}
                        className={`p-4 align-top leading-6 ${comparisonToneText[cell.tone] || "text-slate-300"} ${index === 0 ? "bg-cyan-300/[0.06]" : ""}`}
                      >
                        {cell.text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {footnote && <p className="mt-4 text-xs leading-5 text-slate-400">{footnote}</p>}
      </div>
    </section>
  );
};

const DetailGrid = ({ title, items }) => {
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white">{title}</h2>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-slate-300">
            <CheckCircle className="mb-4 h-6 w-6 text-cyan-300" aria-hidden="true" />
            {item}
          </div>
        ))}
      </div>
    </section>
  );
};

const ProcessDetail = ({ steps }) => {
  if (!steps.length) return null;
  return (
    <section className="bg-slate-900/70 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white">How it works</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <div key={step.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 font-bold text-slate-950">{index + 1}</div>
              <h3 className="mt-6 text-xl font-bold text-white">{step.title}</h3>
              <p className="mt-3 leading-7 text-slate-300">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqSection = ({ faqs }) => {
  if (!faqs.length) return null;
  return (
    <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white">Questions</h2>
      <div className="mt-8 space-y-4">
        {faqs.map((faq) => (
          <div key={faq.question} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <h3 className="text-lg font-bold text-white">{faq.question}</h3>
            <p className="mt-3 leading-7 text-slate-300">{faq.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const LocationsIndexPage = () => {
  const schema = useMemo(
    () => [
      buildLocalBusinessSchema(),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Locations", url: "/locations/" },
      ]),
    ],
    []
  );
  usePageMeta({
    title: "Local AI Consulting Locations | Tier-1.io",
    description: "Tier-1.io provides AI consulting and automation for Cherry Valley, Beaumont, Calimesa, Redlands, and nearby Inland Empire businesses.",
    path: "/locations/",
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow="Locations" title="AI consulting and automation for Inland Empire businesses." description="Local pages for the service areas where Tier-1.io helps owner-led businesses fix messy workflows with practical AI and automation." />
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {locations.map((location) => (
              <a key={location.slug} href={`/${location.slug}/`} className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition hover:-translate-y-1 hover:border-cyan-300/40">
                <MapPin className="h-8 w-8 text-cyan-300" aria-hidden="true" />
                <h2 className="mt-5 text-2xl font-bold text-white">{location.city}, {location.state}</h2>
                <p className="mt-2 font-semibold text-cyan-200">AI consulting in {location.city}</p>
                <p className="mt-3 leading-7 text-slate-300">{location.heroDescription}</p>
                <span className="mt-6 inline-flex items-center font-semibold text-cyan-300">View local AI consulting page <ArrowRight className="ml-2 h-4 w-4" /></span>
              </a>
            ))}
          </div>
        </section>
      </main>
    </Layout>
  );
};

const LocationPage = ({ location }) => {
  const schema = useMemo(
    () => [
      buildLocationSchema(location),
      buildFaqSchema(location.faqs || []),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Locations", url: "/locations/" },
        { name: `${location.city}, ${location.state}`, url: `/${location.slug}/` },
      ]),
    ],
    [location]
  );
  usePageMeta({ title: location.title, description: location.metaDescription, path: `/${location.slug}/`, schema });

  return (
    <Layout>
      <main>
        <PageHero eyebrow={location.heroKicker} title={location.heroTitle} description={location.heroDescription} />
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8">
              <h2 className="text-2xl font-bold text-white">Who we help in {location.city}</h2>
              <ul className="mt-6 space-y-4">
                {location.whoWeHelp.map((item) => (
                  <li key={item} className="flex gap-3 text-slate-300">
                    <CheckCircle className="mt-1 h-5 w-5 flex-none text-cyan-300" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <h3 className="mt-8 font-semibold text-white">Nearby areas</h3>
              <p className="mt-2 text-slate-300">{location.nearbyAreas.join(", ")}</p>
            </div>
            <div className="grid gap-5">
              {location.workflows.map((workflow) => (
                <div key={workflow.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <h3 className="text-xl font-bold text-white">{workflow.title}</h3>
                  <p className="mt-3 leading-7 text-slate-300">{workflow.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <DetailGrid title="Why local businesses work with Tier-1.io" items={location.localProofPoints || []} />
        <FaqSection faqs={location.faqs || []} />
        <CtaSection />
      </main>
    </Layout>
  );
};

const ContactPage = () => {
  const schema = useMemo(() => [buildLocalBusinessSchema()], []);
  usePageMeta({
    title: "Contact Tier-1.io | Book an AI Discovery Call",
    description: "Contact Tier-1.io to book an AI discovery call for practical workflow automation, implementation, custom AI systems, or support.",
    path: "/contact/",
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow="Contact" title="Bring us one messy workflow." description="Send the process that is too manual, slow, or easy to drop. We will help you decide whether AI, automation, process cleanup, or no change is the right move." />
        <ContactSection />
      </main>
    </Layout>
  );
};

const NotFoundPage = () => {
  usePageMeta({
    title: "Page Not Found | Tier-1.io",
    description: "The requested Tier-1.io page could not be found.",
    path: window.location.pathname,
    schema: [],
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow="404" title="That page is not here." description="Head back to the services page or book a discovery call and tell us what you were looking for." />
      </main>
    </Layout>
  );
};

const agentsHref = `/${agentsPillar.slug}/`;

const VoiceAgentsBanner = () => (
  <section className="border-y border-white/10 bg-gradient-to-r from-cyan-400/10 to-transparent py-12">
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">New</p>
        <h2 className="mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Conversational AI voice agents, built on ElevenLabs
        </h2>
        <p className="mt-3 leading-7 text-slate-300">
          Custom voice agents that answer calls, qualify leads, book appointments, and resolve support questions — wired into the tools you already run.
        </p>
      </div>
      <a href={agentsHref} className="inline-flex flex-none items-center justify-center rounded-full border border-cyan-300/40 px-6 py-3 font-semibold text-cyan-200 transition hover:bg-cyan-300/10">
        Explore voice agents
        <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
      </a>
    </div>
  </section>
);

const CapabilityGrid = ({ capabilities }) => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
    <SectionHeading
      eyebrow="What we build"
      title="Voice agents scoped to real work, not demos."
      description="Each agent is built around one workflow and connected to your systems so the conversation updates real records."
    />
    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {capabilities.map((capability) => (
        <div key={capability.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
          <PhoneCall className="h-7 w-7 text-cyan-300" aria-hidden="true" />
          <h3 className="mt-5 text-xl font-bold text-white">{capability.title}</h3>
          <p className="mt-3 leading-7 text-slate-300">{capability.description}</p>
        </div>
      ))}
    </div>
  </section>
);

const AgentIndustryGrid = ({ industries }) => (
  <section className="bg-slate-900/70 py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        eyebrow="By industry"
        title="Voice agents for the way your industry works."
        description="Start from the call flows that matter most in your business. Every page lists the agent types we build and the outcomes they drive."
      />
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {industries.map((industry) => (
          <a key={industry.slug} href={`/${industry.slug}/`} className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition hover:-translate-y-1 hover:border-cyan-300/40">
            <Building2 className="h-8 w-8 text-cyan-300" aria-hidden="true" />
            <h3 className="mt-5 text-xl font-bold text-white">{industry.industry}</h3>
            <p className="mt-3 leading-7 text-slate-300">{industry.heroDescription}</p>
            <span className="mt-6 inline-flex items-center font-semibold text-cyan-300">
              {industry.agents.length} agent types
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const BuildOnSection = ({ buildOn }) => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <SectionHeading eyebrow="How it's built" title={buildOn.title} description={buildOn.description} />
      <ul className="grid gap-4">
        {buildOn.points.map((point) => (
          <li key={point} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-slate-200">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-cyan-300" aria-hidden="true" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const ProcessSteps = () => (
  <section className="bg-slate-900/70 py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        eyebrow="Process"
        title="Useful before impressive."
        description="We scope one call flow, build and integrate it, test against realistic calls, then support and tune it after launch."
      />
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {processSteps.map((step, index) => (
          <div key={step.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 font-bold text-slate-950">{index + 1}</div>
            <h3 className="mt-6 text-xl font-bold text-white">{step.title}</h3>
            <p className="mt-3 leading-7 text-slate-300">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AgentsPillarPage = () => {
  const schema = useMemo(
    () => [
      buildServiceSchema({
        name: "Conversational AI Voice Agent Development",
        slug: agentsPillar.slug,
        url: pageUrl(agentsHref),
        description: agentsPillar.metaDescription,
        serviceType: "Conversational AI development",
        areaServed: nationalAreaServed,
      }),
      buildFaqSchema(agentsPillar.faqs),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Voice Agents", url: agentsHref },
      ]),
      buildOrganizationSchema(),
    ],
    []
  );
  usePageMeta({
    title: agentsPillar.title,
    description: agentsPillar.metaDescription,
    path: agentsHref,
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow={agentsPillar.heroKicker} title={agentsPillar.heroTitle} description={agentsPillar.heroDescription} cta={agentsPillar.cta} />
        <section className="mx-auto max-w-4xl px-4 pt-16 sm:px-6 lg:px-8">
          <p className="text-lg leading-8 text-slate-300">{agentsPillar.intro}</p>
        </section>
        <CapabilityGrid capabilities={agentsPillar.capabilities} />
        <AgentIndustryGrid industries={agentIndustries} />
        <BuildOnSection buildOn={agentsPillar.buildOn} />
        <ProcessSteps />
        <FaqSection faqs={agentsPillar.faqs} />
        <CtaSection />
      </main>
    </Layout>
  );
};

const AgentCard = ({ agent }) => (
  <article className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
    <div className="flex items-start justify-between gap-4">
      <Headphones className="h-7 w-7 flex-none text-cyan-300" aria-hidden="true" />
      {agent.integrations > 0 && (
        <span className="rounded-full border border-cyan-300/30 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-200">
          {agent.integrations} {agent.integrations === 1 ? "integration" : "integrations"}
        </span>
      )}
    </div>
    <h3 className="mt-5 text-xl font-bold text-white">{agent.name}</h3>
    <p className="mt-3 leading-7 text-slate-300">{agent.description}</p>
  </article>
);

const PainPointsBlock = ({ intro, painPoints }) => (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
    <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
      <p className="text-lg leading-8 text-slate-300">{intro}</p>
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8">
        <h2 className="text-xl font-bold text-white">Where the time goes today</h2>
        <ul className="mt-6 space-y-4">
          {painPoints.map((point) => (
            <li key={point} className="flex items-start gap-3 text-slate-300">
              <span className="mt-2 h-2 w-2 flex-none rounded-full bg-cyan-300" aria-hidden="true" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const BuiltOnCallout = () => (
  <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
    <div className="rounded-3xl border border-cyan-300/20 bg-cyan-300/10 p-8">
      <div className="flex items-center gap-3">
        <Bot className="h-6 w-6 text-cyan-300" aria-hidden="true" />
        <h2 className="text-xl font-bold text-white">Built on ElevenLabs Agents</h2>
      </div>
      <p className="mt-3 leading-7 text-cyan-100">
        ElevenLabs provides natural, low-latency speech and turn-taking. Tier-1.io builds the conversation design, integrations, guardrails, and human handoff around it.{" "}
        <a href={agentsHref} className="font-semibold text-white underline-offset-4 hover:underline">
          See how we build conversational AI
        </a>
        .
      </p>
    </div>
  </section>
);

const RelatedIndustries = ({ currentSlug }) => {
  const others = agentIndustries.filter((industry) => industry.slug !== currentSlug).slice(0, 3);
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold text-white">Voice agents for other industries</h2>
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {others.map((industry) => (
          <a key={industry.slug} href={`/${industry.slug}/`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-cyan-300/40">
            <Building2 className="h-7 w-7 text-cyan-300" aria-hidden="true" />
            <h3 className="mt-4 text-lg font-bold text-white">{industry.industry}</h3>
            <span className="mt-4 inline-flex items-center font-semibold text-cyan-300">
              View agents
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
};

const IndustryAgentPage = ({ industry }) => {
  const schema = useMemo(
    () => [
      buildServiceSchema({
        name: `AI Voice Agents for ${industry.industry}`,
        slug: industry.slug,
        url: pageUrl(`/${industry.slug}/`),
        description: industry.metaDescription,
        serviceType: `Conversational AI for ${industry.industry}`,
        areaServed: nationalAreaServed,
      }),
      buildFaqSchema(industry.faqs || []),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Voice Agents", url: agentsHref },
        { name: industry.industry, url: `/${industry.slug}/` },
      ]),
    ],
    [industry]
  );
  usePageMeta({
    title: industry.title,
    description: industry.metaDescription,
    path: `/${industry.slug}/`,
    schema,
  });

  return (
    <Layout>
      <main>
        <PageHero eyebrow={industry.heroKicker} title={industry.heroTitle} description={industry.heroDescription} cta={industry.cta} />
        <PainPointsBlock intro={industry.intro} painPoints={industry.painPoints} />
        <section className="bg-slate-900/70 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white">Voice agents we build for {industry.industry.toLowerCase()}</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {industry.agents.map((agent) => <AgentCard key={agent.name} agent={agent} />)}
            </div>
          </div>
        </section>
        <DetailGrid title="What you get" items={industry.outcomes || []} />
        <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8">
            <div className="flex items-center gap-3">
              <Workflow className="h-6 w-6 text-cyan-300" aria-hidden="true" />
              <h2 className="text-xl font-bold text-white">Connected to your systems</h2>
            </div>
            <p className="mt-3 leading-7 text-slate-300">{industry.integrationsNote}</p>
          </div>
        </section>
        <BuiltOnCallout />
        <FaqSection faqs={industry.faqs || []} />
        <RelatedIndustries currentSlug={industry.slug} />
        <CtaSection />
      </main>
    </Layout>
  );
};

const App = () => {
  const path = normalizePath();
  const service = services.find((item) => item.slug === path);
  const customSolution = customSolutions.find((item) => item.slug === path);
  const location = locations.find((item) => item.slug === path);
  const agentIndustry = agentIndustries.find((item) => item.slug === path);

  if (path === "") return <HomePage />;
  if (path === "services") return <ServicesIndexPage />;
  if (path === "custom-solutions") return <CustomSolutionsPage />;
  if (path === "locations") return <LocationsIndexPage />;
  if (path === "contact") return <ContactPage />;
  if (path === agentsPillar.slug) return <AgentsPillarPage />;
  if (agentIndustry) return <IndustryAgentPage industry={agentIndustry} />;
  if (service) return <ServicePage service={service} />;
  if (customSolution) return <ServicePage service={customSolution} />;
  if (location) return <LocationPage location={location} />;

  return <NotFoundPage />;
};

export default App;
